import React, { useState } from 'react';
import { MapPin, Globe, Clock, Navigation2, Map } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { Feature, OpeningTime } from '@/types/field';
import { format, parseISO } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import MapboxLocation from './MapLocation';

interface Direction {
    maneuver: {
        instruction: string;
    };
    distance: number;
    duration: number;
}

interface FieldInformationProps {
    longitude: number;
    latitude: number;
    address: string;
    what3words: string;
    description: string;
    features: Feature[];
    times: OpeningTime[];
    name: string;
}

const JsonLd = ({ data }: { data: any }) => (
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }} />
);

const FieldInformation: React.FC<FieldInformationProps> = ({
    longitude,
    latitude,
    address,
    what3words,
    description,
    features,
    times,
    name
}) => {
    const [directions, setDirections] = useState<Direction[]>([]);
    const [isLoadingDirections, setIsLoadingDirections] = useState(false);
    const [directionsError, setDirectionsError] = useState<string | null>(null);

    // Function to dynamically render icons
    const renderIcon = (iconName: string) => {
        const IconComponent = (LucideIcons as any)[iconName];
        return IconComponent ? <IconComponent className="w-5 h-5 mr-2 text-brand" /> : null;
    };

    const formatTime = (timeString: string) => {
        const date = parseISO(timeString);
        const utcDate = toZonedTime(date, 'UTC');
        return format(utcDate, 'h:mm a');
    };

    const groupOpeningTimes = (times: OpeningTime[]) => {
        const weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

        // Separate regular days and holidays
        const regularTimes = times.filter(time => !time.isHoliday);
        const holidayTimes = times.filter(time => time.isHoliday);

        // Sort regular times by weekday
        const sortedRegularTimes = [...regularTimes].sort(
            (a, b) => weekdays.indexOf(a.day) - weekdays.indexOf(b.day)
        );

        const groupedTimes = [];
        let currentGroup: OpeningTime[] = [];

        // Group regular times
        sortedRegularTimes.forEach((time, index) => {
            if (
                index === 0 ||
                (time.opening === sortedRegularTimes[index - 1].opening &&
                    time.closing === sortedRegularTimes[index - 1].closing &&
                    time.closed === sortedRegularTimes[index - 1].closed)
            ) {
                currentGroup.push(time);
            } else {
                groupedTimes.push(currentGroup);
                currentGroup = [time];
            }
        });

        if (currentGroup.length > 0) {
            groupedTimes.push(currentGroup);
        }

        // Add holidays as a separate group if they exist
        if (holidayTimes.length > 0) {
            // Modify the holiday times to show "Bank Holidays" instead of the day name
            const modifiedHolidayTimes = holidayTimes.map(time => ({
                ...time,
                day: "Bank Holidays"
            }));

            // Group holidays with the same opening/closing times
            const holidayGroup = [modifiedHolidayTimes[0]];
            groupedTimes.push(holidayGroup);
        }

        return groupedTimes;
    };

    const formatDayRange = (group: OpeningTime[]) => {
        if (group[0].day === "Bank Holidays") return "Bank Holidays";
        if (group.length === 1) return group[0].day;
        return `${group[0].day} - ${group[group.length - 1].day}`;
    };

    const groupedTimes = groupOpeningTimes(times);

    const getWhat3WordsLink = (words: string) => {
        return `https://what3words.com/${words}`;
    };

    const getGoogleMapsLink = (lat: number, lng: number) => {
        return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    };

    const jsonLdData = {
        "@context": "https://schema.org",
        "@type": "Place",
        "name": name,
        "address": {
            "@type": "PostalAddress",
            "streetAddress": address
        },
        "geo": {
            "@type": "GeoCoordinates",
            "latitude": latitude,
            "longitude": longitude
        },
        "description": description,
        "openingHoursSpecification": times.map(time => ({
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": time.day,
            "opens": time.opening,
            "closes": time.closing
        }))
    };

    const MAP_KEY = "pk.eyJ1IjoiYWxhbmNvbGUiLCJhIjoiY2o2d2dwbDNtMXJ6YjJ4bDFmZWxsZHdtdiJ9.PbKLaM4I5rLeJxQiSOxDLw";

    const getDirections = async () => {
        setIsLoadingDirections(true);
        setDirectionsError(null);

        try {
            // Get user's location
            const position = await new Promise<GeolocationPosition>((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(resolve, reject);
            });

            const { latitude: startLat, longitude: startLng } = position.coords;

            // Get directions from Mapbox
            const response = await fetch(
                `https://api.mapbox.com/directions/v5/mapbox/driving/${startLng},${startLat};${longitude},${latitude}?steps=true&geometries=geojson&access_token=${MAP_KEY}`
            );

            if (!response.ok) {
                throw new Error('Failed to fetch directions');
            }

            const data = await response.json();
            if (data.routes && data.routes[0] && data.routes[0].legs) {
                setDirections(data.routes[0].legs[0].steps);
            }
        } catch (error) {
            console.error('Error getting directions:', error);
            setDirectionsError(
                error instanceof GeolocationPositionError 
                    ? 'Please enable location access to get directions' 
                    : 'Failed to get directions. Please try again.'
            );
        } finally {
            setIsLoadingDirections(false);
        }
    };

    const formatDistance = (meters: number): string => {
        if (meters < 1000) {
            return `${Math.round(meters)}m`;
        }
        return `${(meters / 1000).toFixed(1)}km`;
    };

    const formatDuration = (seconds: number): string => {
        const minutes = Math.round(seconds / 60);
        if (minutes < 60) {
            return `${minutes} min`;
        }
        const hours = Math.floor(minutes / 60);
        const remainingMinutes = minutes % 60;
        return `${hours}h ${remainingMinutes}min`;
    };

    <JsonLd data={jsonLdData} />
    return (
        <div className="bg-white shadow-md rounded-lg p-6">
            <h2 className="text-2xl font-bold mb-4">Field Information</h2>

            <div className="space-y-4 mb-6">
                <div className="flex flex-col">
                    <div>
                        <p className="font-semibold mb-2 flex items-center">
                            <Globe className="w-5 h-5 text-gray-500 mr-2" />
                            Location
                        </p>
                        <MapboxLocation
                            latitude={latitude}
                            longitude={longitude}
                            mapboxToken={MAP_KEY}
                            geoJsonPath={`/shapes/${name.toLowerCase().replace(/\s+/g, '')}.json`}
                        />
                        <div className="flex space-x-2 mt-2">
                            <a
                                href={getGoogleMapsLink(latitude, longitude)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-md transition-colors duration-200"
                            >
                                <Map className="w-4 h-4 mr-2" />
                                View on Google Maps
                            </a>
                            <button
                                onClick={getDirections}
                                disabled={isLoadingDirections}
                                className="inline-flex items-center px-3 py-2 bg-brand hover:bg-brand/90 text-white text-sm font-medium rounded-md transition-colors duration-200 disabled:opacity-50"
                            >
                                <Navigation2 className="w-4 h-4 mr-2" />
                                {isLoadingDirections ? 'Getting Directions...' : 'Get Directions'}
                            </button>
                        </div>
                    </div>
                </div>

                {directionsError && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                        {directionsError}
                    </div>
                )}

                {directions.length > 0 && (
                    <div className="mt-4">
                        <h3 className="text-xl font-semibold mb-3">Directions</h3>
                        <div className="bg-gray-50 rounded-md p-4">
                            <ul className="space-y-3">
                                {directions.map((step, index) => (
                                    <li key={index} className="flex items-start">
                                        <span className="flex-shrink-0 w-6 h-6 bg-pink text-white rounded-full flex items-center justify-center text-sm mr-3 mt-0.5">
                                            {index + 1}
                                        </span>
                                        <div>
                                            <p className="text-gray-700">{step.maneuver.instruction}</p>
                                            <p className="text-sm text-gray-500">
                                                {formatDistance(step.distance)} • {formatDuration(step.duration)}
                                            </p>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                )}

                <div className="flex items-start">
                    <MapPin className="w-5 h-5 mr-2 text-gray-500 mt-1" />
                    <div>
                        <p className="font-semibold">Address</p>
                        <p>{address}</p>
                    </div>
                </div>

                <div className="flex items-start">
                    <span className='text-red-600 mr-2'>///</span>
                    <div>
                        <p className="font-semibold">What3Words</p>
                        <a
                            href={getWhat3WordsLink(what3words)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                        >
                            {what3words}
                        </a>
                    </div>
                </div>

                <div className="flex items-start">
                    <div>
                        <h3 className="text-xl font-semibold mb-3">Description</h3>
                        <p dangerouslySetInnerHTML={{ __html: description }}></p>
                    </div>
                </div>
            </div>

            <h3 className="text-xl font-semibold mb-3">Opening Times</h3>
            <ul className="grid grid-cols-1 gap-2">
                {groupedTimes.map((group, index) => (
                    <li key={index} className="flex items-center">
                        <Clock className="w-5 h-5 mr-2 text-brand" />
                        <span className="mr-2 text-black">
                            {formatDayRange(group)}:
                        </span>
                        {group[0].closed ? (
                            <span className="text-red-500">Closed</span>
                        ) : (
                            <>
                                {formatTime(group[0].opening)} - {formatTime(group[0].closing)}
                            </>
                        )}
                    </li>
                ))}
            </ul>

            <h3 className="text-xl font-semibold mt-6 mb-3">Features</h3>
            <ul className="grid grid-cols-1 gap-2">
                {features.map((feature, index) => (
                    <li key={index} className="flex items-center">
                        <span className="mr-2 text-brand">{renderIcon(feature.icon)}</span>
                        {feature.name}
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default FieldInformation;